import { baseService } from "src/store/base.store";
import { executeQuery } from "src/store";
import { wordBankRepository } from "src/features/ai-sessions/repositories/word-bank.repository";
import { WordBankItem } from "src/features/ai-sessions/types";

export type WordBankFilter = {
  id?: string;
  userId?: string;
};

const matchesWordBankFilters = (
  wordBankItem: WordBankItem,
  filters: Partial<WordBankFilter>
): boolean => {
  const { id: _ignored, ...rest } = filters;
  const entries = Object.entries(rest) as [keyof typeof rest, string | undefined][];

  return entries.every(([key, value]) => {
    if (value == null || value === "") return true;
    return (wordBankItem as Record<string, unknown>)[key] === value;
  });
};

const wordBankService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getWordBank: builder.query<WordBankItem[], { where?: WordBankFilter; offset?: string; limit?: number }>({
      queryFn: async ({ where = {}, offset, limit = 10 }) => {
        if (where.id) {
          const { id, ...restFilters } = where;
          const result = await executeQuery(() => wordBankRepository.findById(id));

          if ("error" in result) return { error: result.error } as any;

          const wordBankItem = result.data as WordBankItem | null;
          if (!wordBankItem) return { data: [] } as any;

          return {
            data: matchesWordBankFilters(wordBankItem, restFilters) ? [wordBankItem] : [],
          } as any;
        }

        const whereFirestore = where.userId ? { userId: where.userId } : {};
        const result = await executeQuery(() =>
          wordBankRepository.find({
            where: whereFirestore,
            offset,
            limit,
            orderBy: ["createdAt", "desc"],
          } as any)
        );

        if ("error" in result) return { error: result.error } as any;

        return { data: (result.data as unknown) as WordBankItem[] };
      },
      providesTags: ["WordBank"],
    }),

    countWordBank: builder.query<number, { where?: WordBankFilter }>({
      queryFn: async ({ where = {} }) => {
        if (where.id) {
          const { id, ...restFilters } = where;
          const result = await executeQuery(() => wordBankRepository.findById(id));

          if ("error" in result) return { error: result.error } as any;

          const wordBankItem = result.data as WordBankItem | null;
          return {
            data: wordBankItem && matchesWordBankFilters(wordBankItem, restFilters) ? 1 : 0,
          } as any;
        }

        const whereFirestore = where.userId ? { userId: where.userId } : {};
        return executeQuery(() => wordBankRepository.count(whereFirestore as any)) as any;
      },
      providesTags: ["WordBank"],
    }),

    deleteWordBankItem: builder.mutation<void, string>({
      queryFn: (id) => executeQuery(() => wordBankRepository.delete(id)),
      invalidatesTags: ["WordBank"],
    }),
  }),
});

export const {
  useGetWordBankQuery,
  useCountWordBankQuery,
  useDeleteWordBankItemMutation,
} = wordBankService;
